import { z } from 'zod';
/**
 * The onboarding funnel — public and unauthenticated (spec §3).
 *
 * Mirrors `domain/src/contracts/checkout.ts` on the backend. Validated at the
 * boundary like everything else, and doubly worth it here: this is the only
 * surface where a member spends money.
 */
export const SignupPath = z.enum(['PARENT', 'PRO', 'TSP', 'ORG']);
/**
 * The seven states a session can hold.
 *
 * `PENDING_ACTIVATION` is the one that matters: **payment is not access**.
 * A paid signup sits here until Stripe's subscription webhook lands, so the
 * funnel says "setting up your account" rather than equating a successful card
 * with a usable product. `ACTIVE` is terminal — a webhook redelivery must not
 * walk an activated account backwards.
 */
export const CheckoutStatus = z.enum([
    'DRAFT',
    'AWAITING_PAYMENT',
    'PENDING_ACTIVATION',
    'ACTIVE',
    'DECLINED',
    'INVOICED',
    'ABANDONED',
]);
export const Relationship = z.enum(['mom', 'dad', 'grandmother', 'grandfather', 'prefer_not_to_say']);
export const SignupAccount = z.object({
    /** One field, not two. Names do not reliably split into first and last. */
    fullName: z.string().min(1, 'Entrez votre nom').max(200),
    email: z.string().email('Entrez une adresse courriel valide'),
    /** The server's rule, mirrored so the form can say it before the round trip. */
    password: z.string().min(12, 'Au moins 12 caractères'),
    relationship: Relationship.optional(),
    locale: z.string().optional(),
    /** The unticked newsletter box. Only true is sent; consent is theirs to give. */
    marketingConsent: z.boolean().optional(),
});
/**
 * A year, not an age.
 *
 * The backend's reasoning and it is right: "an age is wrong within twelve
 * months of being typed". The form collects a year and renders an age.
 */
export const ChildInput = z.object({
    name: z.string().min(1).max(120),
    birthYear: z.number().int().min(1900).max(2100).optional(),
    photoAssetId: z.string().uuid().optional(),
});
export const PractitionerInput = z.object({
    profession: z.string().min(1, 'Entrez votre profession').max(160),
    setting: z.string().max(160).optional(),
    licenceBody: z.string().max(160).optional(),
    licenceNumber: z.string().max(80).optional(),
    /** The profile step's slider — self-declared, optional. */
    experienceYears: z.number().int().min(0).max(60).optional(),
});
export const OrganisationKind = z.enum(['SCHOOL', 'CPE', 'CLINIC', 'CIUSSS', 'OTHER']);
export const OrganisationInput = z.object({
    name: z.string().min(1, "Entrez le nom de l'organisation").max(200),
    /** The form's five chips. Defaulted server-side too, so omitting stays legal. */
    type: OrganisationKind.default('OTHER'),
    seatCount: z.number().int().positive('Au moins un siège'),
    billingEmail: z.string().email('Entrez une adresse de facturation valide'),
    billingContactName: z.string().max(200).optional(),
    /** Québec enterprise number — optional, free text. */
    neq: z.string().max(20).optional(),
    /** One line — « Street, city, postal code » as typed. */
    billingAddress: z.string().max(300).optional(),
});
/**
 * Starting a signup. The path decides which extras are required — checked here
 * as well as server-side so the funnel refuses before the round trip.
 */
export const StartCheckout = z
    .object({
    path: SignupPath,
    planKey: z.string().min(1).max(40),
    account: SignupAccount,
    promoCode: z.string().max(40).optional(),
    children: z.array(ChildInput).max(12).default([]),
    /**
     * The second caregiver's first name — persisted on the parent so the
     * sidebar's My Crew widget can show it. Mirrors the backend contract:
     * a name only, no invitation flow (there is no separate account to
     * model yet), blank means the "Your crew" step was skipped.
     */
    coParentName: z.string().min(1).max(120).optional(),
    practitioner: PractitionerInput.optional(),
    organisation: OrganisationInput.optional(),
})
    .superRefine((input, ctx) => {
    if (input.path === 'ORG' && !input.organisation) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['organisation'],
            message: "Le parcours organisation demande les détails de l'organisation",
        });
    }
    if (input.path === 'PRO' && !input.practitioner) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['practitioner'],
            message: 'Le parcours praticien demande une profession',
        });
    }
    // Only the parent path collects children — the server refuses otherwise.
    if (input.path !== 'PARENT' && input.children.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['children'],
            message: 'Seul le parcours parent recueille des enfants',
        });
    }
});
/** Parts per million, because QST is 9.975% and would not survive basis points. */
export const InvoiceTaxLine = z.object({
    code: z.string(),
    /**
     * What the tax was called when it was quoted — « TPS », « TVQ ».
     *
     * The same field the invoice's own tax line carries, and it was missing
     * here: the wire has sent it on a quote all along and Zod dropped it, so the
     * purchase funnel's tax rows had only a code to print. Defaulted rather than
     * required, because 22 of the 50 paid invoices carry tax rows with no label
     * and `taxName` is written to fall back.
     */
    label: z.string().nullable().default(null),
    ratePpm: z.number().int(),
    amountCents: z.number().int(),
});
/**
 * Price → discount → tax, in that order. Taxing before discounting charges tax
 * on money nobody paid.
 *
 * The client never recomputes this. The quote is stored server-side because
 * "the customer agreed to a number on the confirm step"; recomputing at payment
 * time would let a reprice move it underneath them.
 */
export const CheckoutQuote = z.object({
    subtotalCents: z.number().int(),
    discountCents: z.number().int(),
    taxableCents: z.number().int(),
    taxes: z.array(InvoiceTaxLine),
    taxCents: z.number().int(),
    totalCents: z.number().int(),
    currency: z.string().length(3),
});
export const CheckoutSession = z.object({
    id: z.string().uuid(),
    path: SignupPath,
    status: CheckoutStatus,
    planKey: z.string(),
    planName: z.string(),
    seats: z.number().int().positive(),
    quote: CheckoutQuote,
    promoCode: z.string().nullable(),
    /** Set once Stripe has been asked for a hosted Checkout page. */
    paymentUrl: z.string().nullable(),
    declineReason: z.string().nullable(),
    /** Whether the account behind this session can sign in and use the product. */
    grantsAccess: z.boolean(),
    organisationId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
});
/** Null clears an applied code. */
export const ApplyPromo = z.object({ code: z.string().max(40).nullable() });
/**
 * Change the plan on an in-progress checkout — the Confirm-plan tier switcher.
 *
 * Mirrors `ChangeCheckoutPlan` on the backend. Before this contract existed
 * the switcher faked itself by calling `start` again with the same email,
 * which the server refuses with EMAIL_ALREADY_REGISTERED — so a visitor
 * picking a different tier landed on "you already have an account".
 */
export const ChangeCheckoutPlan = z.object({
    planKey: z
        .string()
        .min(1)
        .max(40)
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/),
});
export const PayCheckout = z.object({
    successUrl: z.string().url(),
    cancelUrl: z.string().url(),
});
// ── Plans, for the landing page ─────────────────────────────────────────────
export const BillingPeriod = z.enum(['MONTH', 'QUARTER', 'YEAR']);
/**
 * `GET /api/plans/catalogue?audience=…` — the PUBLIC read the funnel uses.
 *
 * Not `Plan`. The backend refused to make `GET /api/plans` public and was right
 * to: `Plan` carries `versions[]` — every price ever charged, including what 84
 * grandfathered subscribers on the archived legacy annual plan still pay — plus
 * `stripePriceId` on every revision, plus archived plans by default. We asked
 * them to flip a decorator over that. They built a separate contract instead.
 *
 * `sellableVersion` is FLATTENED here: the price a new subscriber would
 * actually be charged, and nothing else. Only plans that have one appear, so an
 * archived plan cannot reach the signup screen at all.
 */
export const PlanCatalogueEntry = z.object({
    id: z.string().uuid(),
    key: z.string(),
    /**
     * Which signup paths this plan is sold on — the SAME four strings as
     * `SignupPath`, so the value a tab filters by is the value the checkout is
     * posted with. No mapping table on either side.
     *
     * A set rather than a widened enum, and the backend's reason is better than
     * the one we gave: a set is what this platform already is. Content, rooms and
     * announcements all carry `audiences`; a plan with a single-valued `group`
     * was the odd one out. It also expresses a plan sold on two paths — the
     * practitioner plan plausibly belongs on both PRO and TSP — which an enum
     * cannot say either way.
     */
    audiences: z.array(SignupPath).min(1),
    position: z.number().nonnegative(),
    highlighted: z.boolean(),
    badge: z.string().nullable(),
    name: z.string(),
    subLabel: z.string().optional(),
    features: z.array(z.string()),
    /** The sellable revision, flattened. */
    planVersionId: z.string().uuid(),
    revision: z.number().int(),
    priceCents: z.number().int().nonnegative(),
    currency: z.string().length(3),
    period: BillingPeriod,
    /**
     * A single charge, not a subscription — the TSP programme. The backend
     * models it as a subscription with `cancel_at_period_end`, so `period` is
     * still the length of what was bought; this flag is what says it does not
     * renew. Defaulted, because the field pre-dates the contract declaring it
     * and Zod strips what is undeclared — which is how the landing card read
     * « per year » on a one-time programme.
     */
    oneTime: z.boolean().default(false),
    mode: z.enum(['FLAT', 'PER_SEAT']),
    /**
     * Volume tiers on a per-seat plan — `unitPriceCents` is the discounted
     * per-seat price for a seat count inside `[minSeats, maxSeats]`, open-ended
     * when `maxSeats` is null. Empty on every flat plan, and empty on a per-seat
     * plan whose tier table awaits pricing confirmation — the live-pricing panel
     * simply shows no discount line then, rather than inventing a percentage.
     */
    tiers: z
        .array(z.object({
        minSeats: z.number().int(),
        maxSeats: z.number().int().nullable(),
        unitPriceCents: z.number().int(),
        /**
         * One price for the whole band — the institutional licence (8 Oct 2026).
         * Set on every tier of a banded plan: the band's price is what is paid,
         * and its `maxSeats` is how many seats the organisation gets.
         */
        flatPriceCents: z.number().int().nullable().default(null),
    }))
        .default([]),
});
export const PlanCatalogue = z.object({
    items: z.array(PlanCatalogueEntry),
    locale: z.string(),
});
//# sourceMappingURL=contracts.js.map